import { POUFF_SNAP_OVERLAP_MM } from '@constants'
import type { AnyID, LinkedItem } from '@types'
import {
  CUSHION_CONTACT_TOLERANCE_MM,
  findCushionVolumePenetration,
  involvesKubeCushion,
} from '@utils/modularSnap/kubeCushion'
import { isKubeCushion } from '@utils/sceneObjects/items/rinconeroUtils'
import { buildItemOBBFromMatrix } from '@utils/sceneObjects/items/itemObbUtils'
import { Matrix4, Quaternion, Vector3 } from 'three'
import { OBB } from 'three/examples/jsm/math/OBB'

const draggedOBB = new OBB()
const neighborOBB = new OBB()
const draggedShrunk = new OBB()
const neighborShrunk = new OBB()

type ItemLike = Pick<LinkedItem, 'width' | 'height' | 'thickness' | 'matrix' | 'type'>

function shrinkHorizontalHalfSizes(obb: OBB, toleranceMm: number, target: OBB): OBB {
  target.center.copy(obb.center)
  target.rotation.copy(obb.rotation)
  target.halfSize.set(
    Math.max(1, obb.halfSize.x - toleranceMm),
    obb.halfSize.y,
    Math.max(1, obb.halfSize.z - toleranceMm),
  )
  return target
}

function shrinkUniformHalfSizes(obb: OBB, toleranceMm: number, target: OBB): OBB {
  target.center.copy(obb.center)
  target.rotation.copy(obb.rotation)
  const t = Math.max(0, toleranceMm)
  target.halfSize.set(
    Math.max(1, obb.halfSize.x - t),
    Math.max(1, obb.halfSize.y - t),
    Math.max(1, obb.halfSize.z - t),
  )
  return target
}

/** Dos unidades siguen siendo vecinas (contacto de cara), no referencias obsoletas. */
export function areItemsStillNeighbors(
  a: ItemLike,
  b: ItemLike,
  toleranceMm = POUFF_SNAP_OVERLAP_MM,
): boolean {
  if (!a.matrix || !b.matrix) return false
  buildItemOBBFromMatrix(a.matrix as Matrix4, a.width, a.height, a.thickness, draggedOBB)
  buildItemOBBFromMatrix(b.matrix as Matrix4, b.width, b.height, b.thickness, neighborOBB)
  shrinkHorizontalHalfSizes(draggedOBB, toleranceMm, draggedShrunk)
  shrinkHorizontalHalfSizes(neighborOBB, toleranceMm, neighborShrunk)
  return draggedShrunk.intersectsOBB(neighborShrunk)
}

/**
 * Penetración real = intersección tras restar el solape permitido.
 * - Sofá/pouff: solape horizontal (contacto lateral).
 * - Si hay cojín involucrado: volumen 3D (contacto de cara OK, interior prohibido).
 */
export function findPenetratingNeighbor(
  itemId: AnyID,
  matrix: Matrix4,
  item: ItemLike,
  items: Map<string, LinkedItem>,
  collisionableItems: Set<AnyID>,
  excludeIds: Iterable<AnyID> = [],
  toleranceMm = POUFF_SNAP_OVERLAP_MM,
): AnyID | null {
  const draggedItem = items.get(String(itemId)) as LinkedItem | undefined

  // Cojín arrastrado: siempre test de volumen 3D contra todos los candidatos.
  if (isKubeCushion(draggedItem?.catalogID)) {
    return findCushionVolumePenetration(
      itemId,
      matrix,
      item,
      items,
      collisionableItems,
      excludeIds,
      CUSHION_CONTACT_TOLERANCE_MM,
    )
  }

  const exclude = new Set(Array.from(excludeIds, (oid) => String(oid)))
  exclude.add(String(itemId))

  buildItemOBBFromMatrix(matrix, item.width, item.height, item.thickness, draggedOBB)

  for (const otherId of collisionableItems) {
    if (exclude.has(String(otherId))) continue
    const other = items.get(String(otherId)) as LinkedItem | undefined
    if (!other?.matrix || String(other.type) === 'linkedSon') continue

    buildItemOBBFromMatrix(
      other.matrix as Matrix4,
      other.width,
      other.height,
      other.thickness,
      neighborOBB,
    )

    // Unidad vs cojín: mismo criterio de volumen (no atravesar el cojín).
    if (involvesKubeCushion(draggedItem?.catalogID, other.catalogID)) {
      shrinkUniformHalfSizes(draggedOBB, CUSHION_CONTACT_TOLERANCE_MM, draggedShrunk)
      shrinkUniformHalfSizes(neighborOBB, CUSHION_CONTACT_TOLERANCE_MM, neighborShrunk)
      if (draggedShrunk.intersectsOBB(neighborShrunk)) {
        return otherId
      }
      continue
    }

    shrinkHorizontalHalfSizes(draggedOBB, toleranceMm, draggedShrunk)
    shrinkHorizontalHalfSizes(neighborOBB, toleranceMm, neighborShrunk)

    if (draggedShrunk.intersectsOBB(neighborShrunk)) {
      return otherId
    }
  }

  return null
}

/**
 * Resuelve movimiento en planta probando: destino completo → solo X → solo Z.
 * Permite deslizar una unidad hasta un hueco sin atravesar en diagonal.
 */
export function resolveFloorDragPosition(
  itemId: AnyID,
  lastMatrix: Matrix4,
  proposedMatrix: Matrix4,
  item: ItemLike,
  items: Map<string, LinkedItem>,
  collisionableItems: Set<AnyID>,
  excludeIds: Iterable<AnyID> = [],
): Matrix4 | null {
  const lastPos = new Vector3()
  const lastRot = new Quaternion()
  const lastScale = new Vector3()
  lastMatrix.decompose(lastPos, lastRot, lastScale)

  const proposedPos = new Vector3()
  proposedMatrix.decompose(proposedPos, new Quaternion(), new Vector3())

  const tryAt = (pos: Vector3): Matrix4 | null => {
    const candidate = new Matrix4().compose(pos, lastRot, lastScale)
    const hit = findPenetratingNeighbor(
      itemId,
      candidate,
      item,
      items,
      collisionableItems,
      excludeIds,
    )
    return hit ? null : candidate
  }

  const full = tryAt(proposedPos)
  if (full) return full

  const xSlide = tryAt(new Vector3(proposedPos.x, lastPos.y, lastPos.z))
  if (xSlide) return xSlide

  const zSlide = tryAt(new Vector3(lastPos.x, lastPos.y, proposedPos.z))
  if (zSlide) return zSlide

  return null
}
